var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageSizeTypeEnum, ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { DexfSettingsUtility, ProductDescriptionUtility, ProductUtility } from '@inter-ikea-kompis/utilities';
import { Image } from '@inter-ikea-kompis/component-image';
import { Text } from '@inter-ikea-kompis/component-text';
import ProductCardVariantsStyle from './ProductCardVariantsStyle.js';
/**
 * Product Card component.
 *
 * @event variantClick Fired when a product variant thumbnail is clicked.
 * @event showMoreClick Fired when the show more button is clicked
 */
export default class ProductCardVariants extends KompisElement {
    constructor() {
        super(...arguments);
        // Array of product variants.
        this.products = null;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Limits the number of variants displayed.
        this.limit = null;
        this.lastVariant = null;
        this.dexfSettings = null;
    }
    /**
     * Render.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        if (!this.products || !this.products.length) {
            return null;
        }
        return html `
			<style>
				${ProductCardVariantsStyle.getStyle({
            theme: this.theme
        })}
			</style>
			${this.getContainer()}
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-image', Image);
        this.customElements.define('kompis-text', Text);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('limit')) {
            this.lastVariant?.focus();
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    /**
     * Returns container.
     *
     * @returns Template.
     */
    getContainer() {
        /* eslint-disable lit-a11y/no-redundant-role */
        return html `
			<ul role="list" class="container">
				${this.getVariants()}${this.getShowMoreButton()}
			</ul>
		`;
        /* eslint-enable lit-a11y/no-redundant-role */
    }
    /**
     * Returns product variants.
     *
     * @returns Template.
     */
    getVariants() {
        const variants = [];
        if (!this.products?.length) {
            return variants;
        }
        const limit = !this.limit ? this.products.length : Math.min(this.limit, this.products.length);
        for (let i = 0; i < limit; i++) {
            const product = this.products[i];
            const description = ProductDescriptionUtility.getDescription({
                product,
                settings: this.getSettings(),
                translations: this.getTranslations(),
                include: {
                    designText: true
                }
            });
            const productImage = ProductUtility.getMainImages(product).find((productImage) => productImage.size === ImageSizeTypeEnum.S5);
            if (productImage) {
                variants.push(html `
					<li>
						<button
							class="variant"
							@click="${(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.dispatchEvent(new CustomEvent('variantClick', { bubbles: true, detail: { product } }));
                }}"
							@keydown="${(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        this.dispatchEvent(new CustomEvent('variantClick', { bubbles: true, detail: { product } }));
                    }
                }}"
							aria-label="${description}"
						>
							<kompis-image .src="${productImage.url}" .productImage="${true}"></kompis-image>
						</button>
					</li>
				`);
                variants.push(html `<div class="divider"></div>`);
            }
        }
        return variants;
    }
    /**
     * Returns more label.
     *
     * @returns Template.
     */
    getShowMoreButton() {
        if (!this.limit || !this.products?.length || this.products.length <= this.limit) {
            return null;
        }
        const label = `+${this.products.length - this.limit}`;
        return html `
			<li>
				<div
					class="showMoreButton"
					role="button"
					@click="${(event) => this.onShowMoreClick(event)}"
					@keydown="${(event) => {
            if (event.key === 'Enter' || event.key === ' ')
                this.onShowMoreClick(event);
        }}"
					aria-expanded="false"
					tabindex="0"
				>
					<span class="visuallyHidden"
						>${this.products.length - this.limit}
						${this.getTranslations().productCardMoreOptionsAvailable}</span
					>
					<kompis-text
						aria-hidden="true"
						.theme="${this.theme}"
						.type="${ThemeFontStyleTypeEnum.bodyMedium}"
						.bold="${true}"
					>
						${label}
					</kompis-text>
				</div>
			</li>
		`;
    }
    onShowMoreClick(event) {
        const productVariantsList = this.shadowRoot.querySelectorAll('.variant');
        this.lastVariant = Array.from(productVariantsList).slice(-1)[0];
        event.preventDefault();
        event.stopPropagation();
        this.dispatchEvent(new CustomEvent('showMoreClick', {
            bubbles: true
        }));
    }
}
__decorate([
    property({ type: Object, required: true })
], ProductCardVariants.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], ProductCardVariants.prototype, "products", void 0);
__decorate([
    property({ type: Object })
], ProductCardVariants.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ProductCardVariants.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ProductCardVariants.prototype, "settings", void 0);
__decorate([
    property({ type: Number })
], ProductCardVariants.prototype, "limit", void 0);
//# sourceMappingURL=ProductCardVariants.js.map